import { useEffect, useRef } from "react";

import { readWorldSessionsChangedAt } from "./sessionTidyClient";

/**
 * Can 80 (the review of 17 Oct 2026): sessions Cleo archives (a tidy she runs,
 * or retire_session) leave the rail without a reload. When a conversation of
 * this World finishes a turn, ask this Desk when it last archived sessions
 * there; later than anything the rail has already caught up with (its mount,
 * or the last change it read the World again for), read the World again. The
 * index object is no clock: the World view rebuilds it whenever conversations
 * change, the end of Cleo's own turn included. Nothing is asked at mount or
 * while no turn finishes.
 */
export function useWorldSessionsChangedRefresh(input: {
  readonly worldKey: string | null | undefined;
  /** The latest finished turn of the World's conversations (any stable key). */
  readonly lastTurnKey: string;
  readonly refresh: () => void;
}): void {
  const { worldKey, lastTurnKey, refresh } = input;
  const caughtUp = useRef<{ worldKey: string; at: number } | null>(null);
  const seen = useRef<string | null>(null);
  useEffect(() => {
    if (typeof worldKey !== "string") return;
    if (caughtUp.current?.worldKey !== worldKey) caughtUp.current = { worldKey, at: Date.now() };
    const previous = seen.current;
    seen.current = lastTurnKey;
    if (previous === null || previous === lastTurnKey) return;
    let live = true;
    void Promise.resolve()
      .then(() => readWorldSessionsChangedAt(worldKey))
      .then((at) => {
        const mark = caughtUp.current;
        if (!live || typeof at !== "number" || mark?.worldKey !== worldKey || at <= mark.at) return;
        caughtUp.current = { worldKey, at };
        refresh();
      })
      .catch(() => {
        // The Desk could not say: the next finished turn asks again.
      });
    return () => {
      live = false;
    };
  }, [worldKey, lastTurnKey, refresh]);
}
